import type { PortalProductEntry, ProductKey, ProductTabRoute } from '@hopdrive/feature-portal-products';
import { capabilityGate, withSettingsTab } from '@hopdrive/feature-portal-products';

import { withSearchParams } from './withSearchParams';
import { PRODUCT_ROUTES } from '@features/products/productRoutes';

/**
 * A product's shell tab list, Settings included, every path carrying the embed's query string.
 *
 * Order matters. `ProductShellLayout` appends the Settings tab itself, but it does that INSIDE
 * the layout — after `Routes.tsx` has already stamped the search onto the tabs it passed down —
 * so a Settings tab the layout added would navigate to the bare `/…/settings` and drop the hash
 * session, the rooftop and every other knob on the first click (finding F7). Appending it here,
 * before `withSearchParams`, gives it the same treatment as every other tab; the layout's own
 * `withSettingsTab` then sees the tab already present and no-ops.
 *
 * The two conditions are the layout's own: `hasSettingsTarget` is `useConfigServiceAdapter().target
 * !== null` (a customer is known for the settings to point at), and the product's entry must hold the
 * `configure` capability. Products with no settings keys (`productHasSettings`) get nothing appended
 * either way.
 */
export const withProductTabs = (
  product: ProductKey,
  entry: PortalProductEntry | undefined,
  tabs: ProductTabRoute[],
  hasSettingsTarget: boolean,
  search: string,
): ProductTabRoute[] => {
  const canConfigure = capabilityGate(entry, 'configure').kind === 'render';
  return withSearchParams(withSettingsTab(PRODUCT_ROUTES, product, tabs, hasSettingsTarget, canConfigure), search);
};
